import React, { useEffect, useState } from 'react';
import {
    Box,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Button,
    FormHelperText,
    Typography,
    Avatar,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    IconButton,
    Chip,
    Grid,
    Tooltip,
    Divider,
    InputAdornment,
    CircularProgress,
    Checkbox
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    PersonAdd as PersonAddIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import axios from 'axios';

// Custom styled avatar component for list display
const UserAvatar = ({ user, size = 40 }) => {
    // Build the complete URL only if an avatar exists
    const avatarUrl = `${process.env.REACT_APP_API_URL}/uploads/avatars/${user.avatar}`;
    const API_BASE_URL = `${process.env.REACT_APP_API_URL}`;
    return (
        <Avatar
            src={avatarUrl}
            alt={user.name}
            sx={{
                width: size,
                border: "2px solid #1976d2",

                fontSize: "14px",
                height: size,
                bgcolor: !user.avatar ? (
                    user.role === 'Admin' ? 'primary.main' :
                        user.role === 'Sales Manager' ? 'secondary.main' :
                            user.role === 'Sales Person' ? 'info.main' : 'grey.500'
                ) : undefined
            }}
        >
            {!user.avatar && user.name?.charAt(0).toUpperCase()}
        </Avatar>
    );
};
const UsersManagement = () => {
    // State for form data
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        role: '',
        status: 'active',
        usertype: 'internal',
        usertypeid: '1',
        avatar: null,

        assigned_sales_persons: '', // 👉 ADD THIS,
        manager_id: ''
    });

    // App state
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [preview, setPreview] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
    const [openDialog, setOpenDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Role mapping for user types
    const roleToUsertypeId = {
        'Admin': 1,
        'Sales Manager': 2,
        'Sales Person': 3,
        'Accountant': 4
    };

    // Role styling mapping
    const roleStyles = {
        'Admin': { color: 'primary', icon: '👑' },
        'Sales Manager': { color: 'secondary', icon: '🔶' },
        'Sales Person': { color: 'info', icon: '🔷' },
        'Accountant': { color: 'success', icon: '📊' }
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };



            // Auto-generate manager_id when role becomes "Sales Manager"
            if (name === 'role' && value === 'Sales Manager') {
                const timestamp = Date.now().toString().slice(-4); // last 4 digits
                updated.manager_id = `MGR${timestamp}`;
            }

            return updated;
        });

    };
    // Handle multi-select changes
    const handleMultiSelectChange = (e) => {
        const { value } = e.target;
        setFormData(prev => ({
            ...prev,
            assigned_sales_persons: typeof value === 'string' ? value.split(',') : value
        }));
    };

    useEffect(() => {
        if (formData.role === 'Sales Manager' && typeof formData.assigned_sales_persons === 'string') {
            const ids = formData.assigned_sales_persons
                .split(',')
                .map(item => item.split(':')[0].trim())
                .filter(Boolean);
            setFormData(prev => ({
                ...prev,
                assigned_sales_persons: ids
            }));
        }
    }, [formData.role, formData.assigned_sales_persons]);


    // Handle phone input change
    const handlePhoneChange = (value) => {
        setFormData(prev => ({ ...prev, phone: value }));
    };

    // Handle avatar file selection
    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, avatar: file }));
            setPreview(URL.createObjectURL(file));
        }
    };

    // Validate email uniqueness
    const checkEmailExists = async (email) => {
        if (!email) return;

        // Check if email is valid
        const url = `https://emailverifier.reoon.com/api/v1/verify?email=${encodeURIComponent(email)}&key=bPxBwPg2sfqbwWhVP4ndNW21FmCaKTYe&mode=power`;
        const res = await axios.get(url);
        if (res.data.status === 'invalid') {
            setEmailError('Invalid email address');
            return;
        }

        try {
            setLoading(true);
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/users/check-email`, {
                params: {
                    email,
                    id: editId || null,
                },
            });

            if (res.data.exists) {
                setEmailError('Email is already in use');
            } else {
                setEmailError('');
            }
        } catch (error) {
            console.error("Email check failed:", error.message);
            // Don't set any error message in UI
        } finally {
            setLoading(false);
        }
    };



    // Reset form to default values
    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            phone: '',
            role: '',
            status: 'active',
            usertype: 'internal',
            usertypeid: '1',
            avatar: null
        });
        setPreview(null);
        setEmailError('');
        setIsEditing(false);
        setEditId(null);
    };

    // Open dialog for creating a new user
    const handleOpenCreateDialog = () => {
        resetForm();
        setOpenDialog(true);
    };

    // Open dialog for editing an existing user
    const handleOpenEditDialog = (user) => {
        setIsEditing(true);
        setEditId(user.id);
        setFormData({
            name: user.name,
            email: user.email,
            password: '', // Leave blank for editing
            phone: user.phone || '',
            role: user.role,
            status: user.status || 'active',
            usertype: user.usertype || 'internal',
            usertypeid: user.usertypeid || '1',
            avatar: null, // Will only update if new file selected
            assigned_sales_persons: user.assigned_sales_persons || [], // Prepopulate assigned sales persons
            manager_id: user.manager_id || ''
        });
        setPreview(user.avatar ? `${process.env.REACT_APP_API_URL}/uploads/${user.avatar}` : null);
        setOpenDialog(true);
    };

    const filteredSalesPersons = users.filter(u =>
        u.role === 'Sales Person' && (!u.manager_id || u.manager_id === formData.manager_id)
    );

    // Close dialog
    const handleCloseDialog = () => {
        setOpenDialog(false);
        resetForm();
    };

    // Handle form submission for create/update
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form fields
        if (!formData.name || !formData.email || (!isEditing && !formData.password) || !formData.role) {
            setAlert({
                open: true,
                message: 'Please fill all required fields',
                severity: 'error'
            });
            return;
        }

        // Check email validation error
        if (emailError) {
            setAlert({
                open: true,
                message: 'Please fix the email issues before submitting',
                severity: 'error'
            });
            return;
        }

        // Add usertypeid based on role
        const updatedFormData = {
            ...formData,
            usertypeid: roleToUsertypeId[formData.role] || '1'
        };

        // Prepare FormData object for submission
        const data = new FormData();
        Object.entries(updatedFormData).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                data.append(key, value);
            }
        });

        try {
            setLoading(true);

            // Get token from localStorage
            const token = localStorage.getItem('authToken');
            if (!token) {
                setAlert({
                    open: true,
                    message: 'Session expired. Please login again.',
                    severity: 'error'
                });
                setTimeout(() => {
                    window.location.href = '/login'; // or use navigate('/login') if using react-router
                }, 1500);
                return;
            }

            // Set request headers
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            };

            // API call
            if (isEditing) {
                // Update user
                await axios.put(`${process.env.REACT_APP_API_URL}/users/${editId}`, data, config);
                setAlert({
                    open: true,
                    message: 'User updated successfully!',
                    severity: 'success'
                });
            } else {
                // Create user
                await axios.post(`${process.env.REACT_APP_API_URL}/users/create`, data, config);
                setAlert({
                    open: true,
                    message: 'User created successfully!',
                    severity: 'success'
                });
            }

            // Reset form and close dialog
            resetForm();
            setOpenDialog(false);
            fetchUsers(); // Refresh user list

        } catch (error) {
            let errorMessage = `Failed to ${isEditing ? 'update' : 'create'} user`;

            if (error.response) {
                if (error.response.status === 401) {
                    errorMessage = 'Session expired. Please login again.';
                    localStorage.removeItem('authToken');
                    setTimeout(() => {
                        window.location.href = '/login';
                    }, 1500);
                } else if (error.response.status === 409) {
                    errorMessage = 'Email is already in use';
                }
            }

            setAlert({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };


    // Delete a user
    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                setLoading(true);
                await axios.delete(`${process.env.REACT_APP_API_URL}/users/${userId}`);
                setAlert({
                    open: true,
                    message: 'User deleted successfully!',
                    severity: 'success'
                });
                fetchUsers();
            } catch (error) {
                setAlert({
                    open: true,
                    message: 'Failed to delete user',
                    severity: 'error'
                });
            } finally {
                setLoading(false);
            }
        }
    };

    // Fetch all users
    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/users`);
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            setAlert({
                open: true,
                message: 'Failed to fetch users',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Filter users based on search term and filters
    const filteredUsers = users.filter(user => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm ||
            user.name?.toLowerCase().includes(searchLower) ||
            user.email?.toLowerCase().includes(searchLower) ||
            user.role?.toLowerCase().includes(searchLower) ||
            user.phone?.toLowerCase().includes(searchLower);

        const matchesRole = !filterRole || user.role === filterRole;
        const matchesStatus = !filterStatus || user.status === filterStatus;

        return matchesSearch && matchesRole && matchesStatus;
    });

    // Handle clearing all filters
    const clearFilters = () => {
        setSearchTerm('');
        setFilterRole('');
        setFilterStatus('');
    };

    // Load users on component mount
    useEffect(() => {
        fetchUsers();
    }, []);

    return (
        <Box sx={{ p: 3, backgroundColor: "#f8fafc", minHeight: '100vh' }}>
            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 1,
                    mb: 1,
                    borderRadius: 2,
                 
                    color: '#000'
                }}
            >
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    User Management
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
                    Create, edit and manage  users
                </Typography>
            </Paper>

            {/* Search & Filter Bar */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'action.active' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={6} md={3} sx={{ width: "20%" }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-role-label">Filter by Role</InputLabel>
                            <Select
                                labelId="filter-role-label"
                                value={filterRole}
                                label="Filter by Role"
                                onChange={(e) => setFilterRole(e.target.value)}
                                startAdornment={<FilterIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />}
                            >
                                <MenuItem value="">All Roles</MenuItem>
                                <MenuItem value="Admin">Admin</MenuItem>
                                <MenuItem value="Sales Manager">Sales Manager</MenuItem>
                                <MenuItem value="Sales Person">Sales Person</MenuItem>
                                <MenuItem value="Accountant">Accountant</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} md={3} sx={{ width: "20%" }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-status-label">Filter by Status</InputLabel>
                            <Select
                                labelId="filter-status-label"
                                value={filterStatus}
                                label="Filter by Status"
                                onChange={(e) => setFilterStatus(e.target.value)}
                                startAdornment={<FilterIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />}
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {(searchTerm || filterRole || filterStatus) && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={clearFilters}
                                    startIcon={<CloseIcon />}
                                >
                                    Clear
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<PersonAddIcon />}
                                onClick={handleOpenCreateDialog}
                                color="primary"
                            >
                                Add User
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, px: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                        {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'} found
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={fetchUsers}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Box>
            </Paper>

            {/* Users Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Avatar</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={40} sx={{ mb: 2 }} />
                                    <Typography>Loading users...</Typography>
                                </TableCell>
                            </TableRow>
                        ) : filteredUsers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                                    <Typography variant="body1" sx={{ mb: 1 }}>No users found</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Try adjusting your search or filters
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredUsers.map(user => (
                                <TableRow
                                    key={user.id}
                                    hover
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                        backgroundColor: user.status === 'inactive' ? 'rgba(0, 0, 0, 0.02)' : 'inherit'
                                    }}
                                >
                                    <TableCell>
                                        <UserAvatar user={user} />
                                    </TableCell>
                                    <TableCell>{user.name}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.phone || '—'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.role}
                                            size="small"
                                            color={roleStyles[user.role]?.color || 'default'}
                                            icon={<Typography sx={{ fontSize: '16px', mr: -0.5 }}>{roleStyles[user.role]?.icon}</Typography>}
                                            sx={{ fontWeight: 500 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.status || 'active'}
                                            size="small"
                                            color={user.status === 'active' ? 'success' : 'error'}
                                            variant="outlined"
                                            sx={{
                                                minWidth: '80px',
                                                fontWeight: 500,
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title="Edit User">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleOpenEditDialog(user)}
                                                    sx={{
                                                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.12)' }
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete User">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteUser(user.id)}
                                                    sx={{
                                                        backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                                        '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.12)' }
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add/Edit User Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h6">
                            {isEditing ? 'Edit User' : 'Create New User'}
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseDialog}
                            sx={{ ml: 'auto' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, alignItems: 'center' }}>
                        {/* Avatar Upload Section */}
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                            <Avatar
                                src={preview}
                                alt="Avatar Preview"
                                sx={{
                                    width: 100,
                                    height: 100,
                                    mb: 2,
                                    mx: 'auto',
                                    fontSize: '3rem',
                                    bgcolor: 'primary.main'
                                }}
                            >
                                {!preview && (formData.name?.charAt(0) || 'U')}
                            </Avatar>

                            <Button
                                variant="outlined"
                                component="label"
                                disabled={loading}
                                startIcon={<AddIcon />}
                                size="small"
                            >
                                {preview ? 'Change Avatar' : 'Upload Avatar'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleAvatarChange}
                                />
                            </Button>
                        </Box>

                        <Grid container spacing={2}>
                            {/* Name */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="name"
                                    label="User Name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                    disabled={loading}
                                />
                            </Grid>

                            {/* Email */}
                            <Grid item xs={12} sm={6} sx={{ width: "50%" }}>
                                <TextField
                                    name="email"
                                    label="Email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => {
                                        handleChange(e);
                                        setEmailError('');
                                    }}
                                    onBlur={(e) => checkEmailExists(e.target.value)}
                                    required
                                    fullWidth
                                    error={Boolean(emailError)}
                                    helperText={emailError}
                                    disabled={loading}
                                />

                            </Grid>

                            {/* Password */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="password"
                                    label={isEditing ? "Password (leave blank to keep current)" : "Password"}
                                    type="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required={!isEditing}
                                    fullWidth
                                    disabled={loading}
                                />
                            </Grid>

                            {/* Phone */}
                            <Grid item xs={12} sm={6} sx={{ width: "50%" }}>
                                <FormControl fullWidth>
                                    <PhoneInput
                                        country={'in'}
                                        value={formData.phone}
                                        onChange={handlePhoneChange}
                                        inputStyle={{ width: '100%' }}
                                        inputProps={{ required: true }}
                                        disabled={loading}
                                    />
                                </FormControl>
                            </Grid>

                            {/* Role */}
                            <Grid item xs={12} sm={6} sx={{ width: "40%" }}>
                                <FormControl fullWidth required>
                                    <InputLabel id="role-label">Role</InputLabel>
                                    <Select
                                        labelId="role-label"
                                        name="role"
                                        value={formData.role}
                                        label="Role"
                                        onChange={handleChange}
                                        disabled={loading}
                                    >
                                        <MenuItem value="Admin">Admin</MenuItem>
                                        <MenuItem value="Sales Manager">Sales Manager</MenuItem>
                                        <MenuItem value="Sales Person">Sales Person</MenuItem>
                                        <MenuItem value="Accountant">Accountant</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Status */}
                            <Grid item xs={12} sx={{ width: "50%" }}>
                                <FormControl fullWidth>
                                    <InputLabel id="status-label">Status</InputLabel>
                                    <Select
                                        labelId="status-label"
                                        name="status"
                                        value={formData.status}
                                        label="Status"
                                        onChange={handleChange}
                                        disabled={loading}
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>


                            {/* {formData.role === 'Sales Person' && (
                                <Grid item xs={12} sm={6} sx={{ width: "50%" }}>
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Assign Sales Manager</InputLabel>
                                        <Select
                                            name="assigned_sales_manager"
                                            value={formData.assigned_sales_manager}
                                            onChange={handleChange}
                                        >
                                            {users.filter(u => u.role === 'Sales Manager').map(manager => (
                                                <MenuItem key={manager.id} value={manager.id}>
                                                    {manager.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )} */}


                            {formData.role === 'Sales Manager' && (
                                <>
                                    <Grid item xs={12} sm={6} sx={{ width: "50%" }}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Assign Sales Persons11</InputLabel>
                                            <Select
                                                multiple
                                                name="assigned_sales_persons"
                                                value={formData.assigned_sales_persons || []}
                                                onChange={handleMultiSelectChange}
                                                renderValue={(selected) =>
                                                    Array.isArray(selected)
                                                        ? selected
                                                            .map(id => {
                                                                const user = users.find(u => u.id === id);
                                                                return user ? user.name : id;
                                                            })
                                                            .join(', ')
                                                        : ''
                                                }
                                            >
                                                {users
                                                    .filter(u =>
                                                        u.role === 'Sales Person' &&
                                                        // Only show unassigned or assigned to current manager
                                                        (!u.manager_id || (isEditing && u.manager_id === editId))
                                                    )
                                                    .map(person => (
                                                        <MenuItem key={person.id} value={person.id}>
                                                            <Checkbox checked={formData.assigned_sales_persons?.includes(person.id) || false} />
                                                            {person.name}
                                                        </MenuItem>
                                                    ))}
                                            </Select>

                                            <FormHelperText>
                                                Only available Sales Persons can be selected. Already assigned ones are hidden.
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                </>
                            )}


                        </Grid>

                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 1 }}>
                    <Button
                        onClick={handleCloseDialog}
                        variant="outlined"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={loading || Boolean(emailError)}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {loading ? 'Processing...' : isEditing ? 'Update User' : 'Create User'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Alert Snackbar */}
            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={() => setAlert({ ...alert, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={alert.severity}
                    onClose={() => setAlert({ ...alert, open: false })}
                    sx={{ width: '100%' }}
                    variant="filled"
                    elevation={6}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UsersManagement;